import type { ComputeResourceCodes } from '../../capabilities/compute';
import { buildDiskPricingWatchFields, buildVendorDeviceMetaWatchFields } from '../../components/vendorDevice';
import { resolvePricingResourceCodes, resolveWatchFieldsFromResourceConfigs } from '../../platform/pipeline-kernel/resolveResourceWatch';
import type { ResourceConfigEntry, ResourceType, SkuModuleMeta } from '../../platform/pipeline-kernel/types';

/** 与 buildVmResourceConfigs / skuResources 投影 code 一致 */
export const vmCpu: ResourceType = {
  requirement: '',
  code: 'CPU',
  description: 'CPU',
  unit: 'Core',
};

export const vmMemory: ResourceType = {
  requirement: '',
  code: 'Memory',
  description: 'Memory',
  unit: 'Gi',
};

export const vmGpu: ResourceType = {
  requirement: '',
  code: 'GPU',
  description: 'GPU',
  unit: 'Card',
};

export const vmBandwidth: ResourceType = {
  requirement: '',
  code: 'Bandwidth',
  description: '网络带宽',
  unit: 'Mbps',
};

export const vmSystemStorage: ResourceType = {
  requirement: '',
  code: 'SystemDisk',
  description: '系统盘',
  unit: 'Gi',
};

export const vmDataStorage: ResourceType = {
  requirement: '',
  code: 'DataDisk',
  description: '数据盘',
  unit: 'Gi',
};

export const vmComputeTemplates = { vmCpu, vmMemory, vmGpu, vmBandwidth, vmSystemStorage, vmDataStorage };

/** 云主机 compute 能力使用的原子资源 code（与 VM_RESOURCE_CONFIGS / 计价投影一致） */
export const VM_COMPUTE_RESOURCE_CODES: ComputeResourceCodes = {
  cpu: vmCpu.code,
  memory: vmMemory.code,
  gpu: vmGpu.code,
  bandwidth: vmBandwidth.code,
};

/**
 * 云主机 innerForm 资源条目：watchFields 与 headless/projections.buildVmResourceConfigs 同源。
 * SKU / 下单通过 platform.useResourceFieldChange 监听这些字段触发计价。
 */
export const VM_RESOURCE_CONFIGS: ResourceConfigEntry[] = [
  {
    ...vmCpu,
    watchFields: [
      'resource.cpu.physicalCards',
      'resource.cpu.physicalCores',
      'resource.cpu.logicalCores',
      'resource.cpu.pricing',
      ...buildVendorDeviceMetaWatchFields('resource.cpuPricing'),
    ],
    displayIds: ['cpuTotal'],
  },
  {
    ...vmMemory,
    watchFields: ['resource.memory', ...buildVendorDeviceMetaWatchFields('resource.memoryPricing')],
    displayIds: ['memory'],
  },
  {
    ...vmGpu,
    watchFields: [
      'resource.gpu',
      'resource.gpu.vendor',
      'resource.gpu.device',
      'resource.gpu.number',
      'resource.gpu.vendorID',
      'resource.gpu.deviceID',
      'resource.gpuPricing',
    ],
    displayIds: ['gpu'],
  },
  {
    ...vmBandwidth,
    watchFields: ['resource.network.Bandwidth', 'resource.network.bandwidthPricing'],
    displayIds: ['bandwidth'],
  },
  {
    ...vmSystemStorage,
    watchFields: ['systemDisk', 'systemDisk.diskSize', ...buildDiskPricingWatchFields('systemDisk')],
    displayIds: ['systemDisk'],
  },
  {
    ...vmDataStorage,
    watchFields: ['dataDisks', ...buildDiskPricingWatchFields('dataDisks.0')],
    displayIdPrefix: 'dataDisk',
  },
];

/** SKU 模块：是否参与 resourceConfigs 投影 / 预置校验 */
export const VM_SKU_MODULE_META: Record<string, SkuModuleMeta> = {
  baseInfo: {
    resource: false,
    presetCheckFields: ['name'],
  },
  network: {
    resource: false,
    presetCheckFields: ['resource.network.vpc.name'],
  },
  systemSetting: {
    resource: true,
    presetCheckFields: ['runStrategy', 'systemDisk'],
    /** 系统盘容量；仅 diskSize 有预填则锁（有 systemDisk 外壳但无容量仍可补填） */
    quantityFields: ['disk'],
    quantityPresetPaths: { disk: ['systemDisk.diskSize'] },
    fieldPresetPaths: {
      'runPolicy.strategy': ['runStrategy'],
    },
  },
  resource: {
    resource: true,
    presetCheckFields: ['resource.cpu', 'resource.memory', 'resource.gpu', 'resource.network.Bandwidth'],
    quantityFields: ['compute.cpu', 'compute.memory', 'compute.gpu', 'compute.bandwidth'],
    quantityPresetPaths: {
      'compute.cpu': ['resource.cpu'],
      'compute.memory': ['resource.memory'],
      'compute.gpu': ['resource.gpu'],
      'compute.bandwidth': ['resource.network.Bandwidth'],
    },
    fieldPresetPaths: {
      /** 仅锁 architecture 自身；勿用父对象 `system`（有 os/version 等也会被当成有预填） */
      'system.architecture': ['system.architecture'],
    },
  },
  service: {
    resource: false,
    presetCheckFields: ['resource.network.service'],
    orderListItemLock: true,
    fieldPresetPaths: {
      'service.list': ['resource.network.service'],
    },
  },
  dataDisk: {
    resource: true,
    presetCheckFields: ['dataDisks'],
    quantityFields: ['storage.list'],
    quantityPresetPaths: { 'storage.list': ['dataDisks'] },
  },
  dns: {
    resource: false,
    orderListItemLock: true,
    presetCheckFields: [
      'resource.network.dnsConfig.nameservers',
      'resource.network.dnsConfig.searches',
      'resource.network.dnsConfig.options',
    ],
    fieldPresetPaths: {
      'network.dns.nameservers': ['resource.network.dnsConfig.nameservers'],
      'network.dns.searches': ['resource.network.dnsConfig.searches'],
      'network.dns.options': ['resource.network.dnsConfig.options'],
    },
  },
};

/** 合并 VM_RESOURCE_CONFIGS 中的 watchFields（去重） */
export function resolveVmResourceWatchFields(configs: ResourceConfigEntry[] = VM_RESOURCE_CONFIGS): string[] {
  return resolveWatchFieldsFromResourceConfigs(configs);
}

/** 参与计价的资源 code */
export function resolveVmPricingResourceCodes(configs: ResourceConfigEntry[] = VM_RESOURCE_CONFIGS): Set<string> {
  return resolvePricingResourceCodes(configs);
}
